import React, { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DollarSign, Edit2, Save, X, Plus, ToggleLeft, ToggleRight,
  Check, Trash2, Star, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export default function PricingManagementPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/pricing/plans`, {
        withCredentials: true
      });
      if (response.data.success) {
        setPlans(response.data.plans);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (plan) => {
    setEditingPlan(plan.name);
    setEditForm({
      displayName: plan.displayName,
      price: plan.price,
      description: plan.description,
      features: [...plan.features],
      workflowExecutions: plan.workflowExecutions,
      activeWorkflows: plan.activeWorkflows,
      integrations: plan.integrations,
      aiCredits: plan.aiCredits,
      stripePriceId: plan.stripePriceId || '',
      isPopular: plan.isPopular
    });
  };

  const cancelEditing = () => {
    setEditingPlan(null);
    setEditForm({});
    setNewFeature('');
  };

  const savePlan = async (planName) => {
    try {
      const response = await axios.put(
        `${API}/admin/pricing/plans/${planName}`,
        editForm,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        fetchPlans();
        cancelEditing();
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    }
  };

  const togglePlanActive = async (planName) => {
    try {
      const response = await axios.post(
        `${API}/admin/pricing/plans/${planName}/toggle`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        fetchPlans();
      }
    } catch (error) {
      console.error('Error toggling plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to toggle plan');
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setEditForm({
        ...editForm,
        features: [...editForm.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setEditForm({
      ...editForm,
      features: editForm.features.filter((_, i) => i !== index)
    });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...editForm.features];
    newFeatures[index] = value;
    setEditForm({
      ...editForm,
      features: newFeatures
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Pricing Management
        </h1>
        <p className="text-muted-foreground">
          Manage your subscription pricing plans. Changes reflect immediately on the landing page.
        </p>
      </div>

      <div className="grid gap-6">
        {plans.map((plan) => (
          <Card key={plan.name} className={!plan.isActive ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-2xl">{plan.displayName}</CardTitle>
                  {plan.isPopular && (
                    <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" /> Most Popular
                    </span>
                  )}
                  {!plan.isActive && (
                    <span className="px-3 py-1 bg-muted text-muted-foreground text-xs font-semibold rounded-full">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {editingPlan === plan.name ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={cancelEditing}
                      >
                        <X className="w-4 h-4 mr-1" /> Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => savePlan(plan.name)}
                      >
                        <Save className="w-4 h-4 mr-1" /> Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => togglePlanActive(plan.name)}
                      >
                        {plan.isActive ? (
                          <><ToggleRight className="w-4 h-4 mr-1" /> Active</>
                        ) : (
                          <><ToggleLeft className="w-4 h-4 mr-1" /> Inactive</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEditing(plan)}
                      >
                        <Edit2 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <CardDescription>
                {editingPlan === plan.name ? (
                  <Input
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    placeholder="Plan description"
                    className="mt-2"
                  />
                ) : (
                  plan.description
                )}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Left Column - Basic Info */}
                <div className="space-y-4">
                  <div>
                    <Label>Display Name</Label>
                    {editingPlan === plan.name ? (
                      <Input
                        value={editForm.displayName}
                        onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                        placeholder="e.g., Quantum Professional"
                      />
                    ) : (
                      <p className="text-lg font-semibold">{plan.displayName}</p>
                    )}
                  </div>

                  <div>
                    <Label>Price (USD/month)</Label>
                    {editingPlan === plan.name ? (
                      <Input
                        type="number"
                        value={editForm.price}
                        onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) })}
                        placeholder="0"
                      />
                    ) : (
                      <p className="text-3xl font-bold text-primary">
                        ${plan.price}
                        <span className="text-sm text-muted-foreground font-normal">/month</span>
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Workflow Executions</Label>
                      {editingPlan === plan.name ? (
                        <Input
                          type="number"
                          value={editForm.workflowExecutions}
                          onChange={(e) => setEditForm({ ...editForm, workflowExecutions: parseInt(e.target.value) })}
                          placeholder="-1 for unlimited"
                        />
                      ) : (
                        <p className="font-medium">
                          {plan.workflowExecutions === -1 ? 'Unlimited' : plan.workflowExecutions.toLocaleString()}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label>Active Workflows</Label>
                      {editingPlan === plan.name ? (
                        <Input
                          type="number"
                          value={editForm.activeWorkflows}
                          onChange={(e) => setEditForm({ ...editForm, activeWorkflows: parseInt(e.target.value) })}
                          placeholder="-1 for unlimited"
                        />
                      ) : (
                        <p className="font-medium">
                          {plan.activeWorkflows === -1 ? 'Unlimited' : plan.activeWorkflows}
                        </p>
                      )}
                    </div>
                  </div>

                  {editingPlan === plan.name && (
                    <>
                      <div>
                        <Label>Stripe Price ID</Label>
                        <Input
                          value={editForm.stripePriceId}
                          onChange={(e) => setEditForm({ ...editForm, stripePriceId: e.target.value })}
                          placeholder="price_xxxxxxxxxxxxx"
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`popular-${plan.name}`}
                          checked={editForm.isPopular}
                          onChange={(e) => setEditForm({ ...editForm, isPopular: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <Label htmlFor={`popular-${plan.name}`}>Mark as "Most Popular"</Label>
                      </div>
                    </>
                  )}
                </div>

                {/* Right Column - Features */}
                <div>
                  <Label className="mb-2 block">Features</Label>
                  <div className="space-y-2">
                    {editingPlan === plan.name ? (
                      <>
                        {editForm.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Input
                              value={feature}
                              onChange={(e) => updateFeature(index, e.target.value)}
                              placeholder="Feature description"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeFeature(index)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <div className="flex items-center gap-2">
                          <Input
                            value={newFeature}
                            onChange={(e) => setNewFeature(e.target.value)}
                            placeholder="Add new feature..."
                            onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                          />
                          <Button
                            size="sm"
                            onClick={addFeature}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <ul className="space-y-2">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {plan.tier && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Tier: {plan.tier} | Internal Name: <code className="px-2 py-1 bg-muted rounded">{plan.name}</code>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {plans.length === 0 && !loading && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Pricing Plans Found</h3>
            <p className="text-muted-foreground mb-4">
              Run the seed script to create initial pricing plans.
            </p>
            <code className="px-4 py-2 bg-muted rounded text-sm">
              python backend/seed_pricing.py
            </code>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
